import React, { FC, useEffect, useState } from "react";
import "reactflow/dist/style.css";
import ReactFlow, {
  addEdge,
  useNodesState,
  useEdgesState,
  Background,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import dagre from "dagre";
import { Flex } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import ProjectPageShell, {
  useProjectContext,
} from "../shared/ProjectPageShell";
import purchaseRequestApis from "../../../api/master/purchaseRequest";

const NODE_WIDTH = 280;
const NODE_HEIGHT = 150;

const CustomNode = ({ data }: any) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!data?.id) return;
    if (data.label === "Purchase Request") {
      navigate(`/pms/project/view-pr/${data.id}`);
    } else if (data.label === "Quotation") {
      navigate(`/pms/project/view-quotation/${data.id}`);
    } else if (data.label === "Purchase Order") {
      navigate(`/pms/project/view-purchase-order/${data.id}`);
    }
  };

  return (
    <div
      style={{
        border: "2px solid #ddd",
        fontSize: "14px",
        padding: "12px",
        borderRadius: "8px",
        width: `${NODE_WIDTH}px`,
        backgroundColor: "white",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
      onClick={handleClick}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "#2557d6",
          width: "10px",
          height: "10px",
          border: "2px solid white",
        }}
      />
      <div
        style={{
          backgroundColor: "rgba(37, 87, 214, 0.1)",
          color: "#2557d6",
          padding: "8px",
          borderRadius: "6px",
          textAlign: "center",
          fontWeight: 600,
          marginBottom: "8px",
        }}
      >
        {data.label}
      </div>
      <div style={{ marginBottom: "4px", fontSize: "13px" }}>
        <strong>Sequence ID:</strong>{" "}
        <span style={{ color: "#666" }}>{data.trackingId}</span>
      </div>
      <div style={{ marginBottom: "4px", fontSize: "13px" }}>
        <strong>Created At:</strong>{" "}
        <span style={{ color: "#666" }}>
          {data.createdAt ? dayjs(data.createdAt).format("DD-MMM-YYYY") : "-"}
        </span>
      </div>
      <div style={{ fontSize: "13px" }}>
        <strong>Created By:</strong>{" "}
        <span style={{ color: "#666" }}>{data.createdBy || "-"}</span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: "#2557d6",
          width: "10px",
          height: "10px",
          border: "2px solid white",
        }}
      />
    </div>
  );
};

const nodeTypes = {
  selectorNode: CustomNode,
};

const dagreLayout = (nodes: any[], edges: any[]) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: "TB",
    ranksep: 150,
    nodesep: 100,
    edgesep: 50,
    marginx: 50,
    marginy: 50,
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((node) =>
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  );
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));

  dagre.layout(g);

  return nodes.map((node) => {
    const nodeData = g.node(node.id);
    return {
      ...node,
      position: {
        x: nodeData.x - NODE_WIDTH / 2,
        y: nodeData.y - NODE_HEIGHT / 2,
      },
      type: "selectorNode",
    };
  });
};

const ZoomControls = () => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  const btnStyle: React.CSSProperties = {
    width: "36px",
    height: "36px",
    border: "1px solid #ddd",
    backgroundColor: "white",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "bold",
    color: "#2557d6",
  };

  return (
    <div
      style={{
        position: "absolute",
        top: "20px",
        right: "20px",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        backgroundColor: "white",
        padding: "8px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        border: "1px solid #ddd",
      }}
    >
      <button onClick={() => zoomIn()} style={btnStyle} title="Zoom In">
        +
      </button>
      <button onClick={() => zoomOut()} style={btnStyle} title="Zoom Out">
        −
      </button>
      <button
        onClick={() => fitView({ padding: 0.2, includeHiddenNodes: true })}
        style={btnStyle}
        title="Fit View"
      >
        ⌂
      </button>
    </div>
  );
};

const TrackPurchaseRequest: FC = () => {
  const { prId } = useParams<{ prId: string }>();
  const navigate = useNavigate();
  const { projectName } = useProjectContext();
  const locationState = (useLocation().state as any) || {};
  const { prNumber, indentNumber } = locationState;

  const [isLoading, setIsLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    fetchTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId]);

  const fetchTracking = async () => {
    if (!prId) return;
    try {
      setIsLoading(true);
      const apiResponse: any = await purchaseRequestApis.prTracking(prId);
      const status = apiResponse?.status;
      const message = apiResponse?.message;
      const payload =
        apiResponse?.response || apiResponse?.data || apiResponse || {};

      if (status) {
        const initialNodes = payload.initialNodes || [];
        const initialEdges = payload.initialEdges || [];
        setNodes(dagreLayout(initialNodes, initialEdges));
        setEdges(initialEdges);
      } else if (message) {
        Swal.fire("Error", message, "error");
      }
    } catch (err) {
      console.error("Error fetching PR tracking:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const onConnect = (params: any) => setEdges((eds) => addEdge(params, eds));

  const onFlowInit = (instance: any) => {
    if (!isLoading && nodes.length > 0) {
      setTimeout(() => {
        instance.fitView({
          padding: 0.2,
          includeHiddenNodes: true,
          minZoom: 0.1,
          maxZoom: 1.2,
        });
      }, 100);
    }
  };

  const actions = (
    <div style={{ color: "#8c8c8c", fontSize: 13 }}>
      {prNumber ? <span>PR # {prNumber}</span> : null}
      {indentNumber ? (
        <span style={{ marginLeft: 12 }}>Indent # {indentNumber}</span>
      ) : null}
    </div>
  );

  return (
    <ProjectPageShell
      headTitle="Track Purchase Request"
      breadcrumb={[
        { label: projectName },
        { label: "Purchase Request" },
        { label: prNumber ? `Track PR # ${prNumber}` : "Track" },
      ]}
      title="Track Purchase Request"
      eyebrow="Purchase Request"
      onBack={() => navigate(-1)}
      actions={actions}
      loading={isLoading}
    >
      {nodes.length === 0 ? (
        <Flex
          align="center"
          justify="center"
          style={{ padding: "80px 0", width: "100%", color: "#8c8c8c" }}
        >
          No tracking data available
        </Flex>
      ) : (
        // ReactFlow requires an explicitly-sized container — this bounded
        // height is the one intentional exception to the shell's natural flow.
        <div
          style={{
            position: "relative",
            height: "calc(100vh - 280px)",
            minHeight: 420,
          }}
        >
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              onInit={onFlowInit}
              minZoom={0.1}
              maxZoom={2}
              zoomOnScroll
              zoomOnPinch
              panOnScroll
              preventScrolling={false}
              fitView
              fitViewOptions={{
                padding: 0.2,
                includeHiddenNodes: true,
                minZoom: 0.1,
                maxZoom: 1.2,
              }}
              style={{ width: "100%", height: "100%" }}
            >
              <Background color="#f0f0f0" gap={20} size={1} />
              <ZoomControls />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
      )}
    </ProjectPageShell>
  );
};

export default TrackPurchaseRequest;
